// Operator › Memory quality › one company (MEMORY-QUALITY-WATCH): what the hourly watch and the
// platform's nightly sweep found in the company's memory, the fix each one suggests, and the
// thresholds the watch uses for this company (platform defaults unless overridden). Platform super
// admins only; the API answers anyone else 404. Findings and thresholds are operator data: the
// company never reads any of it.
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  Input,
  Notice,
  SettingsGroup,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BrainIcon, RotateCcwIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { OpsBody, OpsFrame, RowAction } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { SEVERITY_BADGE, SEVERITY_LABEL } from "./agent-health";

type View = OperationOutput<"operator.memoryQuality.get">;
type Finding = View["findings"][number];
type Threshold = View["thresholds"][number];

const key = (co: string) => ["operator", "memory-quality", co] as const;

const SOURCE_LABEL: Record<Finding["source"], string> = {
  watch: "Hourly watch",
  sweep: "Nightly sweep",
};

export function MemoryQualityPage({ api, companyId }: { api: Api; companyId: string }) {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: key(companyId),
    queryFn: () => api.client.operator.memoryQuality.get({ params: { company_id: companyId } }),
  });
  const put = useMutation({
    mutationFn: (body: { thresholds?: Record<string, number | null>; dismiss?: string[] }) =>
      api.client.operator.memoryQuality.put({ params: { company_id: companyId }, body }),
    onSuccess: (v) => client.setQueryData(key(companyId), v),
    onError: (e) => toast.add({ title: problemText(e, "Not saved. Try again."), type: "error" }),
  });
  if (q.isPending)
    return (
      <OpsFrame>
        <OpsBody>
          <Loading label="Loading memory quality" />
        </OpsBody>
      </OpsFrame>
    );
  if (q.isError)
    return (
      <OpsFrame>
        <OpsBody>
          {q.error instanceof ApiError && q.error.status === 404 ? (
            <EmptyState
              variant="card"
              icon={BrainIcon}
              title="Super admins only"
              description="Memory quality is open to platform super admins."
            />
          ) : (
            <LoadError what="Memory quality" error={q.error} />
          )}
        </OpsBody>
      </OpsFrame>
    );
  const v = q.data;
  const open = v.findings.filter((f) => f.state === "open");
  const closed = v.findings.filter((f) => f.state !== "open");
  return (
    <OpsFrame>
      <OpsBody data-slot="memory-quality">
        <header className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">{v.company.name}: memory quality</h1>
          <InfoTip label="About memory quality">
            What the hourly check and the nightly review found in this company's memory, and the fix
            each suggests. Nothing here is shown to the company.
          </InfoTip>
          <Link
            to="/operator/agent-health"
            className="ml-auto text-sm text-muted-foreground underline-offset-4 hover:underline"
          >
            Back to Agent health
          </Link>
        </header>
        {v.as_of ? (
          <p className="text-xs text-muted-foreground tabular-nums">
            Checked {formatDateTime(v.as_of)}
            {v.metrics ? ` · ${v.metrics.writes} saves that hour` : ""}
          </p>
        ) : null}
        <SettingsGroup title="Needs a look" count={open.length}>
          {open.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-muted-foreground">Nothing open.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-subtle" data-slot="mq-findings">
              {open.map((f) => (
                <li
                  key={f.id}
                  className="group flex flex-col gap-1 px-4 py-2.5"
                  data-finding={f.id}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={SEVERITY_BADGE[f.severity]}>{SEVERITY_LABEL[f.severity]}</Badge>
                    <span className="text-[13px] font-medium break-words">{f.title}</span>
                    <span className="text-xs text-muted-foreground">{SOURCE_LABEL[f.source]}</span>
                    <span className="ml-auto flex items-center gap-1">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatDateTime(f.last_seen_at)}
                      </span>
                      <RowAction
                        icon={XIcon}
                        label={`Dismiss: ${f.title}`}
                        tooltip="Dismiss"
                        disabled={put.isPending}
                        onClick={() => put.mutate({ dismiss: [f.id] })}
                      />
                    </span>
                  </div>
                  {f.suggestion ? (
                    <p className="text-[13px] text-muted-foreground">{f.suggestion}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </SettingsGroup>
        <SettingsGroup title="Thresholds">
          <ul className="flex flex-col divide-y divide-subtle" data-slot="mq-thresholds">
            {v.thresholds.map((t) => (
              <ThresholdRow
                key={t.metric}
                t={t}
                pending={put.isPending}
                onSave={(n) => put.mutate({ thresholds: { [t.metric]: n } })}
              />
            ))}
          </ul>
        </SettingsGroup>
        {closed.length ? (
          <SettingsGroup title="Closed lately" count={closed.length}>
            <ul className="flex flex-col divide-y divide-subtle">
              {closed.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
                  <Badge variant="outline">
                    {f.state === "resolved" ? "Resolved" : "Dismissed"}
                  </Badge>
                  <span className="break-words text-muted-foreground">{f.title}</span>
                </li>
              ))}
            </ul>
          </SettingsGroup>
        ) : null}
        {put.isError ? <Notice tone="danger">Not saved. Try again.</Notice> : null}
      </OpsBody>
    </OpsFrame>
  );
}

function ThresholdRow({
  t,
  pending,
  onSave,
}: {
  t: Threshold;
  pending: boolean;
  onSave: (n: number | null) => void;
}) {
  const [text, setText] = useState(String(t.value));
  const n = Number(text);
  const valid = text.trim() !== "" && Number.isFinite(n) && n >= 0;
  return (
    <li className="flex flex-wrap items-center gap-2 px-4 py-2" data-threshold={t.metric}>
      <span className="min-w-0 flex-1 text-[13px]">
        {t.label}
        <span className="text-muted-foreground">
          {" "}
          · {t.dir === "above" ? "alert above" : "alert below"} ({t.unit})
        </span>
      </span>
      {t.custom ? <Badge variant="outline">Custom</Badge> : null}
      <Input
        aria-label={`${t.label} threshold`}
        className="w-24"
        inputMode="decimal"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button
        size="xs"
        variant="secondary"
        disabled={pending || !valid || n === t.value}
        onClick={() => onSave(n)}
      >
        Save
      </Button>
      {t.custom ? (
        <RowAction
          icon={RotateCcwIcon}
          label={`Reset ${t.label} to ${t.default}`}
          tooltip="Use the default"
          disabled={pending}
          onClick={() => {
            setText(String(t.default));
            onSave(null);
          }}
        />
      ) : null}
    </li>
  );
}
